/**
 * Composant de vue détaillée pour afficher les informations complètes d'un objet touristique
 */

import { LitElement, html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ObjetTouristique, MoyenCommunicationType } from "../types/apidae.d";
import { WidgetLanguage } from "../types/widgets.d";
import { TranslationService } from "../services/TranslationService";
import detailViewStyles from "../styles/detail-view.css?inline";

@customElement("apidae-detail-view")
export class DetailView extends LitElement {
  @property({ type: Object })
  item!: ObjetTouristique;

  @property({ type: String })
  language: WidgetLanguage = WidgetLanguage.FR;

  static styles: CSSResultGroup = unsafeCSS(detailViewStyles);

  private t(key: string): string {
    return TranslationService.translate(key, this.language);
  }

  private getLabel(libelle: any): string {
    if (!libelle) return "";
    return TranslationService.getLabel(libelle, this.language);
  }

  private handleClose() {
    this.dispatchEvent(
      new CustomEvent("close", { bubbles: true, composed: true }),
    );
  }

  private getImageUrl(): string | null {
    if (!this.item.illustrations || this.item.illustrations.length === 0) {
      return null;
    }

    const illustration = this.item.illustrations[0];
    return illustration.traductionFichiers?.[0]?.url || null;
  }

  private formatDate(dateStr: string | undefined): string {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(this.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  render() {
    const imageUrl = this.getImageUrl();
    const nom = this.getLabel(this.item.nom);
    const description = this.getLabel(this.item.presentation?.descriptifCourt);
    const descriptionDetaille = this.getLabel(
      this.item.presentation?.descriptifDetaille,
    );
    const commune = this.item.localisation?.adresse?.commune?.nom;

    return html`
      <div class="detail-container" @click=${(e: Event) => e.stopPropagation()}>
        <button class="close-button" @click=${this.handleClose} title="Fermer">
          ✕
        </button>

        <div class="detail-header">
          ${imageUrl
            ? html`<img class="detail-image" src="${imageUrl}" alt="${nom}" />`
            : html`<div class="detail-image-placeholder">📍</div>`}
          <div class="detail-header-overlay">
            <h1 class="detail-title">${nom}</h1>
            ${commune
              ? html`<div class="detail-location">📍 ${commune}</div>`
              : ""}
          </div>
        </div>

        <div class="detail-content">
          ${description
            ? html`
                <div class="detail-section">
                  <h2 class="section-title">Description</h2>
                  <div class="section-content">${description}</div>
                </div>
              `
            : ""}
          ${descriptionDetaille && descriptionDetaille !== description
            ? html`
                <div class="detail-section">
                  <h2 class="section-title">Description détaillée</h2>
                  <div class="section-content">${descriptionDetaille}</div>
                </div>
              `
            : ""}
          ${this.renderInformations()} ${this.renderContact()}
          ${this.renderOpenings()}
        </div>
      </div>
    `;
  }

  private renderInformations() {
    const type = this.item.type;
    const capacite = (this.item as any).capacite;

    const hasInfo = type || capacite;
    if (!hasInfo) return "";

    return html`
      <div class="detail-section">
        <h2 class="section-title">Informations</h2>
        <div class="info-grid">
          ${type
            ? html`
                <div class="info-item">
                  <div class="info-label">Type</div>
                  <div class="info-value">${type}</div>
                </div>
              `
            : ""}
          ${capacite?.capaciteMaximale
            ? html`
                <div class="info-item">
                  <div class="info-label">Capacité</div>
                  <div class="info-value">
                    ${capacite.capaciteMaximale} personnes
                  </div>
                </div>
              `
            : ""}
        </div>
        <div class="badges-container">
          <span class="badge">${type}</span>
        </div>
      </div>
    `;
  }

  private renderContact() {
    const adresse = this.item.localisation?.adresse;
    const moyens = this.item.informations?.moyensCommunication || [];

    const telephone = moyens.find(
      (m) =>
        m.type?.enumKey === MoyenCommunicationType.TELEPHONE ||
        m.type?.id === 201,
    );
    const email = moyens.find(
      (m) =>
        m.type?.enumKey === MoyenCommunicationType.MAIL || m.type?.id === 204,
    );
    const website = moyens.find(
      (m) =>
        m.type?.enumKey === MoyenCommunicationType.SITE_WEB ||
        m.type?.id === 205,
    );

    const hasContact = adresse || telephone || email || website;
    if (!hasContact) return "";

    return html`
      <div class="detail-section">
        <h2 class="section-title">Contact</h2>
        <div class="contact-info">
          ${adresse
            ? html`
                <div class="contact-item">
                  <span class="contact-icon">📍</span>
                  <div>
                    ${adresse.adresse1 ? html`${adresse.adresse1}<br />` : ""}
                    ${adresse.adresse2 ? html`${adresse.adresse2}<br />` : ""}
                    ${adresse.codePostal} ${adresse.commune?.nom}
                  </div>
                </div>
              `
            : ""}
          ${telephone
            ? html`
                <div class="contact-item">
                  <span class="contact-icon">📞</span>
                  <a
                    href="tel:${telephone.coordonnees?.fr}"
                    class="contact-link"
                  >
                    ${telephone.coordonnees?.fr}
                  </a>
                </div>
              `
            : ""}
          ${email
            ? html`
                <div class="contact-item">
                  <span class="contact-icon">✉️</span>
                  <a
                    href="mailto:${email.coordonnees?.fr}"
                    class="contact-link"
                  >
                    ${email.coordonnees?.fr}
                  </a>
                </div>
              `
            : ""}
          ${website
            ? html`
                <div class="contact-item">
                  <span class="contact-icon">🌐</span>
                  <a
                    href="${website.coordonnees?.fr}"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="contact-link"
                  >
                    ${this.t("website") || "Site web"}
                  </a>
                </div>
              `
            : ""}
        </div>
      </div>
    `;
  }

  private renderOpenings() {
    const periodes = this.item.ouverture?.periodesOuvertures;
    if (!periodes || periodes.length === 0) return "";

    return html`
      <div class="detail-section">
        <h2 class="section-title">${this.t("openingHours") || "Horaires"}</h2>
        <div class="openings">
          ${periodes.slice(0, 3).map(
            (periode) => html`
              <div class="opening-period">
                <div class="opening-dates">
                  ${this.formatDate(periode.dateDebut)}
                  ${periode.dateFin
                    ? html` - ${this.formatDate(periode.dateFin)}`
                    : ""}
                </div>
                ${periode.complementHoraire
                  ? html`
                      <div class="opening-details">
                        ${this.getLabel(periode.complementHoraire)}
                      </div>
                    `
                  : ""}
              </div>
            `,
          )}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-detail-view": DetailView;
  }
}
